import React from "react";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CloseIcon from "@mui/icons-material/Close";

import { useFormik } from "formik";
import * as yup from "yup";

import { usePartnersStore } from "../../store/partnersStore";

interface CreatePartnerFields {
  displayName: string;
  hashtag: string;
  aboutText: string;
  logoUrl: string;
  primaryColor: string;
  accentColor: string;
  discourseGroupName: string;
  discourseCategoryUrl: string;
  sendWelcomeEmail: boolean;
  sendOffboardingEmail: boolean;
}

// String-only keys — the boolean toggles below render as Switches, not TextFields.
type StringField = Exclude<
  keyof CreatePartnerFields,
  "sendWelcomeEmail" | "sendOffboardingEmail"
>;

const initialValues: CreatePartnerFields = {
  displayName: "",
  hashtag: "",
  aboutText: "",
  logoUrl: "",
  primaryColor: "",
  accentColor: "",
  discourseGroupName: "",
  discourseCategoryUrl: "",
  sendWelcomeEmail: true,
  sendOffboardingEmail: true,
};

const hexColor = yup
  .string()
  .matches(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, "Must be a hex color (e.g. #1A2B3C).");

const validationSchema = yup.object({
  displayName: yup.string().required("Display name is required."),
  hashtag: yup
    .string()
    .required("Hashtag is required.")
    .matches(/^[a-zA-Z0-9_]+$/, "Hashtag may only contain letters, numbers, and underscores."),
  aboutText: yup.string(),
  logoUrl: yup.string().url("Must be a valid URL."),
  primaryColor: hexColor,
  accentColor: hexColor,
  discourseGroupName: yup.string(),
  discourseCategoryUrl: yup.string().url("Must be a valid URL."),
  sendWelcomeEmail: yup.boolean(),
  sendOffboardingEmail: yup.boolean(),
});

// Drop empty optional strings so we never PATCH "" over an unset backend field.
const trimmedOrUndefined = (value: string) => {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
};

interface CreatePartnerFormProps {
  onClose: () => void;
}

const CreatePartnerForm: React.FC<CreatePartnerFormProps> = ({ onClose }) => {
  const { createPartner } = usePartnersStore();
  const [errorMessage, setErrorMessage] = React.useState<string>();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values, helpers) => {
      setErrorMessage(undefined);
      try {
        await createPartner({
          displayName: values.displayName.trim(),
          hashtag: values.hashtag.trim(),
          aboutText: trimmedOrUndefined(values.aboutText),
          logoUrl: trimmedOrUndefined(values.logoUrl),
          primaryColor: trimmedOrUndefined(values.primaryColor),
          accentColor: trimmedOrUndefined(values.accentColor),
          discourseGroupName: trimmedOrUndefined(values.discourseGroupName),
          discourseCategoryUrl: trimmedOrUndefined(values.discourseCategoryUrl),
          sendWelcomeEmail: values.sendWelcomeEmail,
          sendOffboardingEmail: values.sendOffboardingEmail,
        });
        helpers.resetForm();
        onClose();
      } catch (err) {
        setErrorMessage(
          `Could not create partner. ${err instanceof Error ? err.message : String(err)}`
        );
      }
    },
  });

  const textFieldProps = (
    name: StringField,
    label: string
  ) => ({
    fullWidth: true,
    id: name,
    name,
    label,
    value: formik.values[name],
    onChange: formik.handleChange,
    error: formik.touched[name] && Boolean(formik.errors[name]),
    helperText: formik.touched[name] && formik.errors[name],
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <DialogTitle
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}
      >
        Create New Partner
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {errorMessage && (
          <Box sx={{ mb: 2 }}>
            <Typography color="error" variant="body2">
              {errorMessage}
            </Typography>
          </Box>
        )}
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <TextField {...textFieldProps("displayName", "Display Name")} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              {...textFieldProps("hashtag", "Hashtag")}
              slotProps={{ input: { startAdornment: <Typography sx={{ mr: 0.5 }}>#</Typography> } }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              {...textFieldProps("aboutText", "About (optional)")}
              multiline
              minRows={2}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField {...textFieldProps("logoUrl", "Logo URL (optional)")} />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField {...textFieldProps("primaryColor", "Primary Color (optional)")} placeholder="#1A2B3C" />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField {...textFieldProps("accentColor", "Accent Color (optional)")} placeholder="#4D5E6F" />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField {...textFieldProps("discourseGroupName", "Discourse Group Name (optional)")} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField {...textFieldProps("discourseCategoryUrl", "Discourse Category URL (optional)")} />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Transactional emails
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  name="sendWelcomeEmail"
                  checked={formik.values.sendWelcomeEmail}
                  onChange={formik.handleChange}
                />
              }
              label="Send welcome email on provisioning"
            />
            <FormControlLabel
              control={
                <Switch
                  name="sendOffboardingEmail"
                  checked={formik.values.sendOffboardingEmail}
                  onChange={formik.handleChange}
                />
              }
              label="Send offboarding email on removal"
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={formik.isSubmitting}>
          {formik.isSubmitting ? "Creating..." : "Create Partner"}
        </Button>
      </DialogActions>
    </form>
  );
};

export const CreatePartnerDialog = () => {
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <IconButton onClick={() => setOpen(true)} title="Create new partner">
        <AddCircleOutlineIcon />
      </IconButton>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <CreatePartnerForm onClose={() => setOpen(false)} />
      </Dialog>
    </>
  );
};
